import type { ReactNode } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardHeader, StatTile } from '../../../components/ui/Card';
import { EmptyState, ErrorState, QueryState } from '../../../components/ui/States';
import { Pagination, usePagination } from '../../../components/ui/Pagination';
import { Nil, Table, THead, Th, Td, Tr } from '../../../components/ui/Table';
import { useSdrOverview } from '../../../hooks/useProjectManagement';
import { formatDayMonth, formatDayMonthTime, formatDayMonthYear, formatTime } from '../../../utils/dateFormat';
import type { SdrBooking, SdrSyncState, SdrWeeklyPoint } from '../../../api/projectManagement';
import {
  CHART_AXIS,
  CHART_GRID,
  CHART_HEIGHT,
  CHART_TOOLTIP,
  BAR_RADIUS_VERTICAL,
  CHART_SERIES,
} from '../../../components/ui/chart';
import { SectionToolbar } from '../../Sales/SectionToolbar';
import { RepInitials, companyLabel, formatShortDate, formatShortDateTime } from '../../Sales/shared';
import { Badge } from '../../../components/ui/Badge';
import { getRep } from '../team';
import { mergeLeadingStub } from './sdrSeries';
import type { MeetingsPeriod } from '../../Sales/meetings/period';
import { plural } from '../formatters';

/** "2026-03-30" → "30 Mar" — timezone-safe for a calendar day. */
export function weekLabel(isoDate: string): string {
  return formatDayMonth(`${isoDate}T00:00:00`);
}

/** "Fillout sync failed for 5 form(s): …" → 5. */
function failedFormCount(error: string | null): number | null {
  const match = error ? /failed for (\d+) form/i.exec(error) : null;
  return match ? Number(match[1]) : null;
}

/**
 * The newest sync run of the source failed (or has been "running" for over an
 * hour): one line, the raw error behind "Details". `delivery` says how new rows
 * reach the page while the sync is down (Fillout: the webhook).
 */
export function SdrSyncProblem({
  sync,
  source = 'Fillout',
  missing = 'Bookings',
  delivery,
}: {
  sync: SdrSyncState;
  source?: string;
  missing?: string;
  delivery?: string;
}) {
  if (!sync.syncFailure) return null;
  const forms = failedFormCount(sync.syncFailure.error);
  const since = sync.syncFailure.since ? ` since ${formatDayMonthTime(sync.syncFailure.since)}` : '';
  const lastGood = sync.lastSyncedAt ? `Last good sync ${formatDayMonthTime(sync.lastSyncedAt)}.` : 'No good sync on record.';
  return (
    <ErrorState
      compact
      title={`${source} sync failing${since}${forms ? `: ${forms} ${forms === 1 ? 'form' : 'forms'}` : ''}`}
      error={delivery ? `${lastGood} ${delivery}` : `${lastGood} ${missing} since then may be missing.`}
      details={sync.syncFailure.error ? `${formatDayMonthTime(sync.syncFailure.at)}: ${sync.syncFailure.error}` : undefined}
    />
  );
}

/** Fixed widths, the same on every page. Matches Demo bookings: booked, who, company, AE, meeting on, status. */
const SDR_TABLE_COLS = ['6.5rem', 'auto', '14rem', '4rem', '9rem', '7rem'];

export function SdrBookingsTable({ bookings }: { bookings: SdrBooking[] }) {
  const { pageItems, ...paging } = usePagination(bookings);
  // Older APIs send neither field: the columns stay out rather than read "·" on every row.
  const withMeeting = bookings.some((b) => b.meeting !== undefined);
  const withAe = bookings.some((b) => b.aeSlug !== undefined);
  return (
    <>
      <Table className="tabular-nums" colWidths={[SDR_TABLE_COLS[0]!, SDR_TABLE_COLS[1]!, SDR_TABLE_COLS[2]!, ...(withAe ? [SDR_TABLE_COLS[3]!] : []), ...(withMeeting ? [SDR_TABLE_COLS[4]!, SDR_TABLE_COLS[5]!] : [])]}>
        <THead>
          <tr>
            <Th>Booked</Th>
            <Th>Who</Th>
            <Th>Company</Th>
            {withAe && <Th align="center" title="The AE whose personal Fillout form was used">AE</Th>}
            {withMeeting && <Th title="The Calendly demo it became (same person, booked within 30 days of the form)">Meeting on</Th>}
            {withMeeting && <Th>Status</Th>}
          </tr>
        </THead>
        <tbody>
          {pageItems.map((b) => (
            <Tr key={b.submissionId}>
              <Td className="text-text-secondary whitespace-nowrap">{formatShortDate(b.day)}</Td>
              <Td className="text-text-primary">
                <div className="break-words">{b.personName ?? <Nil />}</div>
                {b.personEmail && <div className="text-body-xs text-text-tertiary break-all">{b.personEmail}</div>}
                {b.source === 'confirmed_without_form' && (
                  <div className="text-body-xs text-text-tertiary" title="No Fillout form was submitted; Walter confirmed an SDR booked this meeting">
                    confirmed by Walter, no form
                  </div>
                )}
              </Td>
              <Td className="text-text-primary">{b.companyName ? companyLabel(b.companyName) : <Nil />}</Td>
              {withAe && <Td align="center">{b.aeSlug ? <RepInitials rep={getRep(b.aeSlug)} /> : <Nil />}</Td>}
              {withMeeting && (
                <Td className="whitespace-nowrap text-text-secondary">
                  {b.meeting ? formatShortDateTime(b.meeting.startAt) : <span className="text-body-xs text-text-tertiary">no Calendly demo yet</span>}
                </Td>
              )}
              {withMeeting && (
                <Td>
                  {b.meeting?.status === 'canceled' ? (
                    <Badge>Cancelled</Badge>
                  ) : b.meeting && new Date(b.meeting.startAt).getTime() > Date.now() ? (
                    <span className="text-body-xs text-text-secondary">Upcoming</span>
                  ) : b.meeting ? (
                    <span className="text-body-xs text-text-tertiary">Past</span>
                  ) : null}
                </Td>
              )}
            </Tr>
          ))}
        </tbody>
      </Table>
      <Pagination {...paging} onPage={paging.setPage} />
    </>
  );
}

/** How new SDR bookings reach the page: the Fillout webhook, with the nightly sync as the back-fill. */
function deliveryLine(newest: string | null | undefined): string {
  return `New bookings still arrive through the Fillout webhook the moment a form is submitted${
    newest ? ` (newest ${formatDayMonthTime(newest)})` : ''
  }; the nightly sync only back-fills submissions the webhook missed, so only those could be absent.`;
}

/** Meetings → SDR meetings booked: one meeting per submission of an SDR's personal Fillout form. */
export function SdrMeetings({
  leading,
  period,
  periodControl,
}: {
  leading?: ReactNode;
  period: MeetingsPeriod;
  periodControl?: ReactNode;
}) {
  const query = useSdrOverview({ from: period.from, to: period.to });
  const data = query.data;

  return (
    <div className="space-y-4">
      <SectionToolbar
        leading={leading}
        meta={
          data && (
            <>
              From Fillout
              {data.newestSubmissionAt ? ` · newest ${formatDayMonthTime(data.newestSubmissionAt)}` : ''}
              {data.syncRunning
                ? ` · sync running since ${formatTime(data.syncRunning.since)}`
                : data.lastSyncedAt
                  ? ` · synced ${formatDayMonthTime(data.lastSyncedAt)}`
                  : ''}
            </>
          )
        }
        info={
          data && (
            <>
              One meeting per submission of Patrick&rsquo;s, Nadia&rsquo;s or Andrew&rsquo;s personal Fillout form, since{' '}
              {formatDayMonthYear(`${data.startDay}T00:00:00`)} ({data.totals.sinceStart} in total). Brussels time.
              {data.excluded.tests + data.excluded.duplicates > 0 &&
                ` ${data.excluded.tests} test and ${data.excluded.duplicates} repeat submissions (same person within 30 days) are not counted.`}{' '}
              Each submission reaches the OS through the Fillout webhook as it is made; the nightly Fillout sync (07:00)
              re-reads every form and back-fills anything the webhook missed.
              {data.definition && <> {data.definition} Demo bookings counts SDR meetings the same way.</>}
            </>
          )
        }
        actions={periodControl}
      />
      <QueryState query={query} errorTitle="Couldn’t load SDR meetings" loadingRows={4}>
        {(d) => {
          const inPeriod = d.period ?? null;
          const rows = inPeriod?.bookings ?? d.recentBookings;
          const weeklyCard = (
<Card padding="md">
              <CardHeader
                title="Meetings booked per week"
                description={
                  period.id === 'week'
                    ? 'This week and the 3 before'
                    : `Every week of the ${period.label} (${plural(d.weeklySeries.length, 'week')}), the first and last cut to the period`
                }
              />
              <WeeklyChart data={d.weeklySeries} />
            </Card>
          );
          return (
            <>
              <SdrSyncProblem sync={d} delivery={deliveryLine(d.newestSubmissionAt)} />
              {inPeriod ? (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                  {/* One tile, one story: the period's count, with this week and this month as sub-lines. */}
                  <StatTile
                    label="SDR meetings"
                    value={inPeriod.count}
                    sub={
                      <>
                        <span className="block">{capitalise(period.label)} · Fillout personal forms</span>
                        <span className="mt-2 block">
                          <span className="tabular-nums text-text-secondary">{d.totals.thisWeek}</span> this week ·{' '}
                          {d.totals.lastWeek} last week
                          {weekDiff(d.totals.thisWeek, d.totals.lastWeekToDate)}
                        </span>
                        <span className="block">
                          <span className="tabular-nums text-text-secondary">{d.totals.thisMonth}</span> this month ·{' '}
                          {d.totals.lastMonth} last month
                        </span>
                      </>
                    }
                    tooltip="Same count and window as the SDR meetings tile on Demo bookings. This week is measured so far, against the same weekdays of last week."
                  />
                  <div className="lg:col-span-2">{weeklyCard}</div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <StatTile
                      label="This week"
                      value={d.totals.thisWeek}
                      sub={`${d.totals.lastWeek} last week · Fillout`}
                      change={weekChange(d.totals.thisWeek, d.totals.lastWeekToDate)}
                      tooltip="The change compares this week so far with the same weekdays of last week, so a Monday is never measured against a full week."
                    />
                    <StatTile label="This month" value={d.totals.thisMonth} sub={`${d.totals.lastMonth} last month · Fillout`} />
                  </div>
                  {weeklyCard}
                </>
              )}

              <Card padding="md">
                <CardHeader
                  title={inPeriod ? `Bookings · ${period.label}` : 'Recent bookings'}
                  description={inPeriod ? undefined : 'Newest 25'}
                />
                {rows.length === 0 ? (
                  <EmptyState title={inPeriod ? `No SDR bookings ${period.label}` : 'No bookings yet'} compact />
                ) : (
                  <SdrBookingsTable bookings={rows} />
                )}
              </Card>
            </>
          );
        }}
      </QueryState>
    </div>
  );
}

/**
 * This week so far against the same weekdays of last week. No pill when the API
 * doesn't give the like-for-like figure, or when the two are equal.
 */
function weekChange(thisWeek: number, lastWeekToDate: number | undefined) {
  if (lastWeekToDate === undefined || thisWeek === lastWeekToDate) return undefined;
  const diff = thisWeek - lastWeekToDate;
  return {
    value: `${diff > 0 ? '+' : '−'}${Math.abs(diff)} vs same days last week`,
    trend: diff > 0 ? ('up' as const) : ('down' as const),
  };
}

/** " · +1 vs same days" when this week so far differs from last week's same weekdays. */
function weekDiff(thisWeek: number, lastWeekToDate: number | undefined): string {
  if (lastWeekToDate === undefined || thisWeek === lastWeekToDate) return '';
  const diff = thisWeek - lastWeekToDate;
  return ` · ${diff > 0 ? '+' : '−'}${Math.abs(diff)} vs same days`;
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** "12–13 Sep" for a week the period cuts short, "21 Sep" for a whole one. */
function bucketLabel(p: SdrWeeklyPoint): { tick: string; tooltip: string } {
  const from = p.from ?? p.weekStart;
  const to = p.to;
  const full = !to || (from === p.weekStart && Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`) === 6 * 86_400_000);
  if (full) return { tick: weekLabel(from), tooltip: `Week of ${weekLabel(from)}` };
  const range = from === to ? weekLabel(from) : `${weekLabel(from)} – ${weekLabel(to)}`;
  return { tick: range, tooltip: `${range} (part of the week of ${weekLabel(p.weekStart)} inside the period)` };
}

function WeeklyChart({ data }: { data: SdrWeeklyPoint[] }) {
  const chartData = mergeLeadingStub(data).map((p) => {
    const l = bucketLabel(p);
    return { week: l.tick, tooltip: l.tooltip, meetings: p.total };
  });

  return (
    <div style={{ width: '100%', height: CHART_HEIGHT.default }}>
      <ResponsiveContainer>
        <BarChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
          <CartesianGrid {...CHART_GRID} />
          <XAxis dataKey="week" {...CHART_AXIS} interval="preserveStartEnd" minTickGap={8} />
          <YAxis {...CHART_AXIS} allowDecimals={false} />
          <Tooltip
            {...CHART_TOOLTIP}
            labelFormatter={(label, payload) => String(payload?.[0]?.payload?.tooltip ?? `Week of ${label}`)}
          />
          <Bar dataKey="meetings" name="Meetings" fill={CHART_SERIES[0]} maxBarSize={24} radius={[...BAR_RADIUS_VERTICAL]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
